import React from 'react'
import PropTypes from 'prop-types'
import { isDev } from 'lib/env'
import { isFunction, merge } from 'lodash'
import * as Nope from 'lib/utils/validation/Nope'
import { setInfoBreadcrumb } from 'lib/sentry'
import { fetchPolicy } from 'lib/apolloFetchPolicy'

import { Query } from '@apollo/client/react/components'
import Input from 'components/Forms/FormElements/Input'
import SelectSearch from 'components/Forms/FormElements/SelectSearch'
import Select from 'components/Forms/FormElements/Select'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import { Field } from 'formik'
import NumberField from 'components/Forms/FormElements/NumberField'
import Checkbox from 'components/Forms/FormElements/Checkbox'
import PhoneNumberField from 'components/Forms/FormElements/PhoneNumberField'

export const getFieldInitialValues = (fields) => {
    return fields.reduce((result, field) => {
        result[field.name] = field.initialValue || ''

        if (field.type && field.type === 'boolean') {
            result[field.name] = field.initialValue || false
        }

        return result
    }, {})
}

export const getFieldsForFieldSchema = (t, fieldFunctions, overrides) => {
    const fieldSchema = Object.entries(fieldFunctions).map(([key, value]) => {
        try {
            let props = value(t, key)

            if (overrides && overrides[key]) {
                props = merge(props, overrides[key])
            }

            return {
                name: key,
                ...props,
            }
        } catch (e) {
            setInfoBreadcrumb(`Failed to create field schema object for field named '${key}'`)
            throw e
        }
    })

    setRequiredFields(fieldSchema)

    return fieldSchema
}

export const getFieldsValidationSchema = (fieldSchema) => {
    const validationSchema = fieldSchema.reduce((result, field) => {
        result[field.name] = field.validation
        return result
    }, {})

    return Nope.schema(validationSchema)
}

export const setRequiredFields = (fieldSchema) => {
    fieldSchema.forEach(field => {
        if (field.required) {
            field.fieldProps = field.fieldProps || {}
            field.fieldProps.required = true

            if (field.type === 'boolean') {
                field.validation = field.validation.oneOf([true], 'formElements:validation.required').required('formElements:validation.required')
            } else {
                field.validation = field.validation.required('formElements:validation.required')
            }
        }
    })
}

class FormBuilder extends React.Component {
    createField (form, fieldSchema, fieldData) {
        const { initialValues } = form
        const { name, inputType } = fieldSchema
        const split = name.split('.')

        if ((split.length === 1 && typeof initialValues[name] === 'undefined') ||
            (split.length === 2 && typeof initialValues[split[0]][split[1]] === 'undefined')) {
            if (isDev()) console.log('initial', initialValues)
            throw new Error(`Field "${name}" missing from form.initialValues`)
        }

        fieldSchema.fieldProps = fieldSchema.fieldProps || {}
        if (typeof fieldSchema.fieldProps.required === 'undefined') {
            fieldSchema.fieldProps.required = form.extendedActions.isFieldRequired(name, form.values)
        }

        switch (inputType) {
        case 'tel':
            return this.createTelField(fieldSchema, form)
        case 'text':
            return this.createTextField(fieldSchema, form)
        case 'number':
            return this.createNumberField(fieldSchema)
        case 'select':
            return this.createSelectField(fieldSchema, form, fieldData)
        case 'checkbox':
            return this.createCheckboxField(fieldSchema)

        default:
            throw new Error('Invalid field schema type:', inputType)
        }
    }

    createTextField (fieldSchema, form) {
        const { name, fieldProps } = fieldSchema
        const { validate } = fieldProps
        let validateFn = null
        if (validate) {
            validateFn = (value) => validate(value, form.values)
        }

        return {
            type: 'text',
            component: Input,
            name,
            ...fieldProps,
            validate: validateFn
        }
    }

    createTelField (fieldSchema) {
        const { name, fieldProps } = fieldSchema

        return {
            type: 'tel',
            name,
            ...fieldProps,
        }
    }

    createNumberField (fieldSchema) {
        const { name, fieldProps } = fieldSchema

        return {
            type: 'number',
            name,
            ...fieldProps,
        }
    }

    createCheckboxField (fieldSchema) {
        const { name, fieldProps } = fieldSchema

        return {
            type: 'checkbox',
            name,
            children: (props) => {
                return (
                    <Checkbox
                        {...fieldProps}
                        {...props}
                    />
                )
            }
        }
    }

    handleFieldChange (e, form, fieldSchema) {
        const { handleChange, setFieldValue } = form
        const { resetFieldsOnChange } = fieldSchema

        if (resetFieldsOnChange) {
            resetFieldsOnChange.forEach(function (fieldName) {
                setFieldValue(fieldName, '')
            })
        }

        handleChange(e)
    }

    createSelectField (fieldSchema, form, fieldData) {
        const { builderValues, shouldRenderSearchSelect } = this.props
        const {
            name,
            fieldProps,
            query,
            requireFields,
        } = fieldSchema

        const { values } = form

        return {
            type: 'select',
            name,
            children: (props) => {
                const requiresAnotherField = requireFields && requireFields.find(fieldName => !values[fieldName])

                const renderSelect = (renderProps) => {
                    if (shouldRenderSearchSelect) {
                        return (
                            <SelectSearch
                                onChange={e => this.handleFieldChange(e, form, fieldSchema)}
                                fieldSchema={fieldSchema}
                                {...props}
                                {...fieldProps}
                                {...fieldData}
                                {...renderProps}
                            />
                        )
                    } else {
                        return (
                            <Select
                                onChange={e => this.handleFieldChange(e, form, fieldSchema)}
                                {...props}
                                {...fieldProps}
                                {...fieldData}
                                {...renderProps}
                            />
                        )
                    }
                }

                if (query && !requiresAnotherField) {
                    return (
                        <Query
                            query={query.query}
                            variables={query.variables ? query.variables({ values, builderValues }) : {}}
                            fetchPolicy={query.fetchPolicy || fetchPolicy.CACHE_FIRST}
                        >
                            {({ loading, data }) => {
                                return renderSelect({
                                    loading,
                                    options: [
                                        ...(fieldProps.options || []),
                                        ...(query.getResultValue(data) || [])
                                    ],
                                })
                            }}
                        </Query>
                    )
                }

                const options = requiresAnotherField ? [] : (
                    [
                        ...(fieldData.options || []),
                        ...(fieldProps.options || [])
                    ]
                )

                return renderSelect({
                    options,
                })
            }
        }
    }

    renderField = (field) => {
        switch (field.type) {
        case 'tel':
            return this.renderTelField(field)
        case 'number':
            return this.renderNumberField(field)
        default:
            return <Field key={field.name} {...field} />
        }
    }

    renderNumberField = (field) => {
        const fieldProps = { ...field }
        delete fieldProps.type

        return (
            <NumberField
                key={field.name}
                {...fieldProps}
            />
        )
    }

    renderTelField = (field) => {
        const fieldProps = { ...field }
        delete fieldProps.type

        return (
            <PhoneNumberField
                key={field.name}
                {...fieldProps}
            />
        )
    }

    renderFieldsWithLayout = (fields) => {
        const { fieldsLayoutSchema } = this.props

        let groupFields

        return fieldsLayoutSchema.layout.map((item, i) => {
            switch (item.type) {
            case 'render':
                return isFunction(item.render) ? item.render() : item.render
            case 'h3':
                return <h3 key={item.id}>{item.text}</h3>
            case 'formElementGroup':
                groupFields = fields.filter(field => item.fields.includes(field.name))
                return groupFields && (
                    <FormElementGroup
                        key={item.fields.join(' ').trim() || i}
                        title={item.title}
                        cssClass={item.classNames}
                    >
                        {groupFields.map(field => this.renderField(field))}
                    </FormElementGroup>
                )
            default:
                throw Error(`Invalid field layout schema type: ${item.type}`)
            }
        })
    }

    render () {
        const {
            children,
            fieldsSchema,
            form,
            fieldsData,
        } = this.props

        const fields = fieldsSchema.fields.map((field) => {
            return this.createField(form, field, fieldsData[field.name] || {})
        })

        return children({
            fields,
            renderFieldsWithLayout: this.renderFieldsWithLayout,
            renderField: this.renderField,
        })
    }
}

FormBuilder.propTypes = {
    form: PropTypes.object.isRequired,
    fieldsSchema: PropTypes.object.isRequired,
    fieldsLayoutSchema: PropTypes.object.isRequired,
    fieldsData: PropTypes.object,
    builderValues: PropTypes.object,
}

PropTypes.defaultValues = {
    builderValues: {},
    fieldsData: {},
}

export default FormBuilder
